import dayjs from 'dayjs';
import type { BarChartOptions } from '~/components/nrjx/chart/new/bar/index.vue';

export const useExploreBarChart = () => {
  // Stores
  const {
    consumptionResponse,
    productionResponse,
    loadingProduction,
    temperatureResponse,
    loadingTemperature,
    comparisonResponse,
    areasToCompare,
    consumptionType,
  } = storeToRefs(chronologicalStore());
  const { aggregationLevel, areaId, showTemperature } = storeToRefs(exploreStore());
  const { getArea } = areaStore();

  // Composables
  const { formatNumberToIsoNumber } = useNumbers();
  const { getFormattedDateFromAggregateLevel } = useChart();
  const { t } = useI18n();
  const { getTailwindColor } = useTailwind();
  const { areaProductionUnit } = coreAreaHelper();

  const options = computed(
    (): BarChartOptions => ({
      height: 'h-[70vh]',
      y: {
        unit: consumptionType.value === 'production' ? 'kWh / ' + areaProductionUnit(areaId.value) : 'kWh',
      },
      y2: {
        unit: '°C',
      },
      tooltip: (index) => {
        const item = consumptionResponse.value?.items[index];
        const temperature = temperatureResponse.value?.items.find((temperature) =>
          dayjs(temperature.date).isSame(dayjs(item?.start), 'day'),
        )?.average;

        if (consumptionType.value === 'production') {
          return productionTooltip({ index, temperature });
        } else if (consumptionType.value === 'consumption' && areasToCompare.value.length > 0) {
          return comparisonTooltip({ index });
        } else {
          return consumptionTooltip({ index, temperature });
        }
      },
    }),
  );

  const productionTooltip = (options: { index: number; temperature: number | null | undefined }) => {
    const item = consumptionResponse.value?.items[options.index];
    const productionItem = productionResponse.value?.items.find((productionItem) =>
      dayjs(item?.start).isSame(productionItem.production.start, 'day'),
    );
    const productionIsAvailable = productionItem?.production.quantity ? true : false;
    const area = getArea(areaId.value);

    const tooltip = {
      title: t('global.epi'),
      subtitle:
        formatNumberToIsoNumber(productionItem?.consumption_per_unit.quantity ?? 0, 2) +
        ' kWh / ' +
        (area?.production_unit?.symbol ?? '--'),
      body: [
        {
          title: t('global.production'),
          loading: loadingProduction.value,
          value: productionIsAvailable
            ? formatNumberToIsoNumber(productionItem?.production.quantity ?? 0) + ' ' + (area?.production_unit?.symbol ?? '--')
            : t('global.not_available').toLocaleLowerCase(),
        },
        {
          title: t('global.consumption'),
          value: productionIsAvailable
            ? formatNumberToIsoNumber(productionItem?.consumption.quantity ?? 0) + ' ' + 'kWh'
            : t('global.not_available').toLocaleLowerCase(),
        },
        {
          title: t('global.average_power'),
          value: productionIsAvailable
            ? formatNumberToIsoNumber(productionItem?.avg_power ?? 0) + ' ' + 'kW'
            : t('global.not_available').toLocaleLowerCase(),
        },
      ],
      footer: getFormattedDateFromAggregateLevel(item?.start ?? new Date(), aggregationLevel.value),
    };

    if (aggregationLevel.value === 'day' && showTemperature.value) {
      tooltip.body?.push({
        title: t('global.temperature'),
        loading: loadingTemperature.value,
        value: options.temperature ? `${options.temperature} °C` : t('global.not_available').toLocaleLowerCase(),
      });
    }

    return tooltip;
  };

  const consumptionTooltip = (options: { index: number; temperature: number | null | undefined }) => {
    const item = consumptionResponse.value?.items[options.index];
    const productionItem = productionResponse.value?.items.find((productionItem) =>
      dayjs(item?.start).isSame(productionItem.production.start, 'day'),
    );
    const area = getArea(areaId.value);

    const tooltip = {
      title: t('global.consumption'),
      subtitle: formatNumberToIsoNumber(item?.consumption.quantity ?? 0, Number(item?.consumption.quantity) < 10 ? 2 : 0) + ' ' + 'kWh',
      body: [
        {
          title: t('global.average_power'),
          value: formatNumberToIsoNumber(item?.avg_power ?? 0, Number(item?.avg_power) < 10 ? 2 : 0) + ' ' + 'kW',
        },
        {
          title: t('global.cost'),
          value: formatNumberToIsoNumber(item?.consumption.cost ?? 0, Number(item?.consumption.cost) < 10 ? 2 : 0) + ' ' + '€',
        },
        {
          title: t('global.production'),
          loading: loadingProduction.value,
          value: productionItem?.production.quantity
            ? formatNumberToIsoNumber(productionItem?.production.quantity ?? 0) + ' ' + (area?.production_unit?.symbol ?? '--')
            : t('global.not_available').toLocaleLowerCase(),
        },
      ],
      footer: getFormattedDateFromAggregateLevel(item?.start ?? new Date(), aggregationLevel.value),
    };

    if (aggregationLevel.value === 'day' && showTemperature.value) {
      tooltip.body?.push({
        title: t('global.temperature'),
        value: options.temperature ? `${options.temperature} °C` : t('global.not_available').toLocaleLowerCase(),
      });
    }

    return tooltip;
  };

  const comparisonTooltip = (options: { index: number }) => {
    const item = comparisonResponse.value?.reference.items[options.index];
    const principalArea = getArea(areaId.value);

    return {
      body: [
        {
          title: principalArea?.name ?? '',
          value: formatNumberToIsoNumber(item?.consumption.quantity ?? 0) + ' ' + 'kWh',
          color: getTailwindColor('emerald-500'),
        },
        ...(comparisonResponse.value?.compared_to.map((compareArea) => {
          const area = getArea(compareArea.area_id);
          const color = areasToCompare.value.find((area) => area.id === compareArea.area_id)?.color;
          const consumptionItem = compareArea.consumption.items.find((consumptionItem) =>
            dayjs(consumptionItem.start).isSame(dayjs(item?.start), 'day'),
          );

          return {
            title: area?.name ?? '',
            value: formatNumberToIsoNumber(consumptionItem?.consumption.quantity ?? 0) + ' ' + 'kWh',
            color: color,
          };
        }) ?? []),
      ],
      footer: getFormattedDateFromAggregateLevel(item?.start ?? new Date(), aggregationLevel.value),
    };
  };

  return {
    options,
  };
};
